import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import RecruiterSidebar from '../components/RecruiterSidebar';
import { API } from '../config';
import '../styles/theme.css';

const CATEGORIES = [
  { key: 'interview',  label: 'Interview',   color: 'badge-green',  icon: 'fa-calendar-check' },
  { key: 'rejection',  label: 'Rejection',   color: 'badge-red',    icon: 'fa-times-circle' },
  { key: 'offer',      label: 'Offer',       color: 'badge-purple', icon: 'fa-trophy' },
  { key: 'general',    label: 'General',     color: 'badge-blue',   icon: 'fa-envelope' },
];

const MERGE_FIELDS = [
  '{{applicant_name}}', '{{job_title}}', '{{company_name}}',
  '{{interview_date}}', '{{interview_time}}', '{{interview_location}}',
];

function CategoryBadge({ category }) {
  const c = CATEGORIES.find(x => x.key === category) || CATEGORIES[3];
  return <span className={`badge ${c.color}`}><i className={`fas ${c.icon}`} style={{ marginRight: 4 }}></i>{c.label}</span>;
}

export default function EmailTemplates() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [templates, setTemplates] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [selected, setSelected] = useState(null);   // template being viewed/edited
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({ name: '', category: 'general', subject: '', body: '' });
  const [saving, setSaving] = useState(false);
  const [filterCat, setFilterCat] = useState('all');
  const token = localStorage.getItem('token');
  const tier = localStorage.getItem('user_tier') || 'starter';
  const isPro = tier === 'professional' || tier === 'business';

  useEffect(() => { if (isPro) fetchAll(); else setLoading(false); }, [isPro]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [tRes, dRes] = await Promise.all([
        fetch(`${API}/email-templates/`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/email-templates/defaults`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (tRes.ok) setTemplates(await tRes.json());
      if (dRes.ok) setDefaults(await dRes.json());
    } catch {}
    finally { setLoading(false); }
  };

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 4000);
  };

  const openNew = () => {
    setForm({ name: '', category: 'general', subject: '', body: '' });
    setEditMode(false);
    setSelected(null);
    setShowForm(true);
  };

  const openEdit = (t) => {
    setForm({ name: t.name, category: t.category, subject: t.subject, body: t.body });
    setEditMode(true);
    setSelected(t);
    setShowForm(true);
  };

  const useDefault = (d) => {
    setForm({ name: d.name, category: d.category, subject: d.subject, body: d.body });
    setEditMode(false);
    setSelected(null);
    setShowForm(true);
  };

  const insertMergeField = (field) => {
    setForm(f => ({ ...f, body: f.body + field }));
  };

  const handleSave = async () => {
    if (!form.name || !form.subject || !form.body) return showMsg('Please fill in all fields.', 'error');
    setSaving(true);
    try {
      const url = editMode ? `${API}/email-templates/${selected.id}` : `${API}/email-templates/`;
      const method = editMode ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      showMsg(editMode ? 'Template updated.' : 'Template created.');
      setShowForm(false);
      fetchAll();
    } catch (e) { showMsg(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await fetch(`${API}/email-templates/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      showMsg('Template deleted.');
      if (selected?.id === id) setSelected(null);
      fetchAll();
    } catch { showMsg('Failed to delete.', 'error'); }
  };

  const handleSend = (t) => {
    // Open mailto with raw template — recruiter fills in details
    const mailto = `mailto:?subject=${encodeURIComponent(t.subject)}&body=${encodeURIComponent(t.body)}`;
    window.open(mailto, '_blank');
  };

  const filtered = filterCat === 'all' ? templates : templates.filter(t => t.category === filterCat);

  return (
    <div className="portal-layout">
      <RecruiterSidebar />
      <main className="portal-main">
        <div className="portal-topbar">
          <span className="topbar-title">Email Templates</span>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={toggleTheme}>
              <i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`}></i>
            </button>
            {isPro && (
              <button className="btn btn-primary btn-sm" onClick={openNew}>
                <i className="fas fa-plus"></i> New Template
              </button>
            )}
          </div>
        </div>

        <div className="portal-content">
          <div className="page-header">
            <h1>
              Email Templates
              <span className="badge badge-blue" style={{ fontSize: '0.7rem', verticalAlign: 'middle', marginLeft: '0.5rem' }}>Professional</span>
            </h1>
            <p>Create reusable email templates for interviews, offers, and rejections</p>
          </div>

          {msg.text && (
            <div className={`alert alert-${msg.type === 'error' ? 'error' : 'success'}`}>
              <i className={`fas ${msg.type === 'error' ? 'fa-exclamation-circle' : 'fa-check-circle'}`}></i> {msg.text}
            </div>
          )}

          {!isPro ? (
            <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
              <i className="fas fa-lock" style={{ fontSize: '3rem', color: 'var(--text-muted)', marginBottom: '1rem', display: 'block' }} />
              <h3 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Professional Feature</h3>
              <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
                Email Templates are available on the Professional plan (R499/month).
              </p>
              <button className="btn btn-primary" onClick={() => navigate('/pricing')}>
                <i className="fas fa-crown"></i> Upgrade to Professional
              </button>
            </div>
          ) : loading ? (
            <div className="empty-state">
              <i className="fas fa-spinner fa-spin" style={{ opacity: 1, color: 'var(--primary)' }}></i>
              <p>Loading templates...</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: showForm ? '1fr 420px' : '1fr', gap: '1.5rem', alignItems: 'start' }}>
              <div>
                {/* Starter templates */}
                {templates.length === 0 && (
                  <div className="card" style={{ marginBottom: '1.5rem' }}>
                    <div className="card-header">
                      <span className="card-title">
                        <i className="fas fa-magic" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>
                        Starter Templates
                      </span>
                      <span className="badge badge-gray">Click to use</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem', paddingTop: '0.5rem' }}>
                      {defaults.map((d, i) => (
                        <div key={i} onClick={() => useDefault(d)} style={{
                          padding: '1rem', borderRadius: 8, border: '1px solid var(--border)',
                          cursor: 'pointer', transition: 'border-color 0.15s, background 0.15s',
                          background: 'var(--surface-2)',
                        }}
                          onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--primary)'}
                          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                        >
                          <CategoryBadge category={d.category} />
                          <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)', marginTop: '0.5rem' }}>{d.name}</div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Click to customise</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Filter tabs */}
                {templates.length > 0 && (
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                    {[{ key: 'all', label: 'All' }, ...CATEGORIES].map(c => (
                      <button key={c.key}
                        className={`btn btn-sm ${filterCat === c.key ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => setFilterCat(c.key)}>
                        {c.label}
                        {c.key !== 'all' && <span className="badge badge-gray" style={{ marginLeft: 4, fontSize: '0.7rem' }}>
                          {templates.filter(t => t.category === c.key).length}
                        </span>}
                      </button>
                    ))}
                    <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={openNew}>
                      <i className="fas fa-plus"></i> New
                    </button>
                  </div>
                )}

                {/* Template list */}
                {filtered.length === 0 && templates.length > 0 && (
                  <div className="empty-state"><i className="fas fa-filter"></i><p>No templates in this category.</p></div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {filtered.map(t => (
                    <div key={t.id} className="card" style={{
                      padding: '1.25rem 1.5rem', cursor: 'pointer',
                      border: selected?.id === t.id ? '1px solid var(--primary)' : '1px solid var(--border)',
                      transition: 'border-color 0.15s',
                    }} onClick={() => setSelected(selected?.id === t.id ? null : t)}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.95rem' }}>{t.name}</span>
                            <CategoryBadge category={t.category} />
                          </div>
                          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                            <i className="fas fa-envelope" style={{ marginRight: 4 }}></i>{t.subject}
                          </div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                            {new Date(t.created_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                          <button className="btn btn-primary btn-sm" onClick={() => handleSend(t)} title="Send via email client">
                            <i className="fas fa-paper-plane"></i>
                          </button>
                          <button className="btn btn-secondary btn-sm" onClick={() => openEdit(t)} title="Edit">
                            <i className="fas fa-edit"></i>
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(t.id)} title="Delete">
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>

                      {/* Expanded preview */}
                      {selected?.id === t.id && (
                        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Preview</div>
                          <pre style={{
                            whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.875rem',
                            color: 'var(--text-secondary)', lineHeight: 1.7, margin: 0,
                            background: 'var(--surface-2)', padding: '1rem', borderRadius: 8,
                          }}>{t.body}</pre>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {templates.length === 0 && defaults.length === 0 && (
                  <div className="empty-state">
                    <i className="fas fa-envelope-open-text"></i>
                    <h3>No templates yet</h3>
                    <p>Create your first email template to speed up candidate communication.</p>
                    <button className="btn btn-primary" style={{ marginTop: '1rem' }} onClick={openNew}>
                      <i className="fas fa-plus"></i> Create Template
                    </button>
                  </div>
                )}
              </div>

              {/* Form panel */}
              {showForm && (
                <div className="card" style={{ position: 'sticky', top: '1rem' }}>
                  <div className="card-header">
                    <span className="card-title">
                      <i className={`fas ${editMode ? 'fa-edit' : 'fa-plus'}`} style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>
                      {editMode ? 'Edit Template' : 'New Template'}
                    </span>
                    <button className="modal-close" onClick={() => setShowForm(false)}><i className="fas fa-times"></i></button>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Template Name</label>
                    <input className="form-control" placeholder="e.g. Interview Invite" value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Category</label>
                    <select className="form-control" value={form.category}
                      onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                      {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Subject</label>
                    <input className="form-control" placeholder="Email subject line" value={form.subject}
                      onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Body</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.5rem' }}>
                      {MERGE_FIELDS.map(mf => (
                        <button key={mf} className="btn btn-ghost btn-sm" style={{ fontSize: '0.72rem', padding: '0.2rem 0.5rem' }}
                          onClick={() => insertMergeField(mf)} title="Insert merge field">
                          {mf}
                        </button>
                      ))}
                    </div>
                    <textarea className="form-control" rows="12" value={form.body}
                      onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                      style={{ fontFamily: 'monospace', fontSize: '0.82rem' }} />
                    <div className="form-hint">Click a merge field above to insert it at the cursor position.</div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                    <button className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                    <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                      <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-save'}`}></i>
                      {saving ? 'Saving...' : 'Save Template'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
